<template>
  <div class="w-full">
    <div 
      class="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-500 transition-colors"
      :class="{ 'border-blue-500 bg-blue-50': isDragging }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleFileDrop"
    >
      <div v-if="!selectedFile && !conversionStore.currentTask">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p class="mt-4 text-lg text-gray-700">拖放PDF文件到这里，或者</p>
        <button 
          @click="triggerFileInput" 
          class="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          选择文件
        </button>
        <input 
          ref="fileInput" 
          type="file" 
          accept=".pdf" 
          class="hidden" 
          @change="handleFileSelect"
        />
        <p class="mt-4 text-sm text-gray-500">
          支持的文件类型: PDF (最大 {{ formatFileSize(conversionStore.settings.max_file_size) }})
        </p>

     

      </div>


    
      
      <div v-else-if="selectedFile && !conversionStore.loading && !conversionStore.currentTask">
        <div class="flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div class="ml-3 text-left">
            <p class="text-lg font-medium text-gray-900">{{ selectedFile.name }}</p>
            <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
          </div>
        </div>

        <div style="text-align: center;margin-top: 20px;">
          增强选项：
          <label class="mr-2" v-for="(option, index) in conversionOptions" :key="index">
            <input type="checkbox"
              v-model="selectedConversionOptions"
              :value="option.value"
              class="mr-1" />
            {{ option.text }}
          </label>
      </div>
        
        <div class="flex justify-center gap-3">
          <button 
            @click="uploadFile" 
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            开始转换
          </button>
          <button 
            @click="clearSelectedFile" 
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            取消
          </button>
        </div>
      </div>
      
      <div v-else-if="conversionStore.loading">
        <div class="flex flex-col items-center">
          <svg class="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="mt-4 text-lg text-gray-700">正在上传文件...</p>
        </div>
      </div>
      <div v-else-if="conversionStore.currentTask">
        <div class="flex flex-col items-center">
          <div v-if="conversionStore.currentTask.status === 'pending' || conversionStore.currentTask.status === 'processing'">
            <svg class="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="mt-4 text-lg text-gray-700">正在转换文件...</p>
          </div>

          <div v-else-if="conversionStore.currentTask.status === 'completed'" class="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="mt-4 text-lg text-gray-700">转换完成!</p>
            <p class="text-md text-gray-600 mt-2">{{ conversionStore.currentTask.filename }}</p>
            
            <div class="flex justify-center mt-4 gap-3">
              <a 
                :href="`https://pdf2doc.toolhero.top/api/download/${conversionStore.currentTask.id}`" 
                target="_blank"
                class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                下载文件
              </a>
              <button 
                @click="resetConverter" 
                class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                转换新文件
              </button>
            </div>
          </div>

          <div v-else-if="conversionStore.currentTask.status === 'failed'" class="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p class="mt-4 text-lg text-gray-700">转换失败</p>
            <p class="text-md text-red-600 mt-2">{{ conversionStore.currentTask.error || '未知错误' }}</p>
            
            <button 
              @click="resetConverter" 
              class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              重试
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="conversionStore.error" class="mt-4 p-3 bg-red-100 text-red-700 rounded">
      {{ conversionStore.error }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useConversionStore } from '../stores/conversion';
import { useAuthStore } from '../stores/auth';

const authStore = useAuthStore();

const conversionStore = useConversionStore();
const fileInput = ref(null);
const selectedFile = ref(null);
const isDragging = ref(false);
const statusCheckInterval = ref(null);

const conversionOptions = [{text:'删除水印背景',value:1}]

onMounted(() => {
  conversionStore.getSettings();
});

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndSetFile(file);
  }
};

const handleFileDrop = (event) => {
  isDragging.value = false;
  
  const file = event.dataTransfer.files[0];
  if (file) {
    validateAndSetFile(file);
  }
};

const validateAndSetFile = (file) => {
  // 验证文件类型
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    conversionStore.error = '只支持PDF文件';
    return;
  }
  
  // 验证文件大小
  if (file.size > conversionStore.settings.max_file_size) {
    conversionStore.error = `文件大小不能超过 ${formatFileSize(conversionStore.settings.max_file_Size)}`;
    return;
  }
  
  selectedFile.value = file;
  conversionStore.error = null;
};

const selectedConversionOptions = ref(false);


const uploadFile = async () => {

  if(!authStore.isAuthenticated){
     conversionStore.error = `请先登陆后转换，谢谢！`;
    return;
  }
  if (!selectedFile.value) return;
  
  let option=0;
  console.log(222,selectedConversionOptions.value)
  if (selectedConversionOptions.value==1){
    option=1;
  }

  try {
    await conversionStore.uploadFile(selectedFile.value,option);
    startStatusCheck();
  } catch (error) {
    console.error('上传失败', error);
  }
};

const startStatusCheck = () => {
  if (statusCheckInterval.value) clearInterval(statusCheckInterval.value);
  
  // 每2秒检查一次状态
  statusCheckInterval.value = setInterval(async () => {
    if (!conversionStore.currentTask) {
      clearInterval(statusCheckInterval.value);
      return;
    }
    
    try {
      await conversionStore.checkStatus(conversionStore.currentTask.id);
      
      // 如果已完成或失败，停止检查
      if (['completed', 'failed'].includes(conversionStore.currentTask.status)) {
        clearInterval(statusCheckInterval.value);
      }
    } catch (error) {
      console.error('检查状态失败', error);
    }
  }, 2000);
};

const clearSelectedFile = () => {
  selectedFile.value = null;
  fileInput.value = '';
  conversionStore.error = null;
};

const resetConverter = () => {
  clearSelectedFile();
  conversionStore.clearCurrentTask();
  
  if (statusCheckInterval.value) {
    clearInterval(statusCheckInterval.value);
  }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
</script>
